import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/** "Add project" — drops a new row directly onto the planner board. Full
 * project setup (client, budget, goals…) still happens on the Project page;
 * this is deliberately a fast, minimal entry point. */
export function AddProjectDialog({ open, onClose, buGroups, onCreate }) {
  const [name, setName] = useState("");
  const [bu, setBu] = useState(buGroups?.[1] || "BU1");
  const [stage, setStage] = useState("active");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onCreate({ name: name.trim(), business_unit: bu, planner_stage: stage });
      setName("");
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="planner-add-project-dialog">
        <DialogHeader>
          <DialogTitle>Add project</DialogTitle>
          <DialogDescription>Drops a new row onto the timeline — full setup happens later on the Project page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-overline">Project name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aria Smart Kettle" className="rounded-sm mt-1" data-testid="planner-add-project-name" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-overline">Business unit</Label>
              <Select value={bu} onValueChange={setBu}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="planner-add-project-bu"><SelectValue /></SelectTrigger>
                <SelectContent>{(buGroups || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="planner-add-project-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="waitlist">Waitlist</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button size="sm" disabled={!name.trim() || saving} onClick={submit} className="rounded-sm bg-[#F54800] hover:bg-[#D93D00] text-white" data-testid="planner-add-project-submit">
            {saving ? "Adding…" : "Add project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Manage people" — every person currently on any visible planner project,
 * grouped by project, with an inline % allocation input per assignment. */
export function ManagePeopleDialog({ open, onClose, projects, onSetAllocation }) {
  const rows = (projects || []).filter((p) => (p.team || []).length > 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="planner-manage-people-dialog">
        <DialogHeader>
          <DialogTitle>Manage people</DialogTitle>
          <DialogDescription>Set each person's % time allocation per project — summed into the studio-capacity chips on the BU cards above.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {rows.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No one is assigned to a project on this board yet.</div>}
          {rows.map((p) => (
            <div key={p.id}>
              <div className="text-[11px] font-semibold text-slate-700 mb-1.5">{p.name}</div>
              <div className="space-y-1.5">
                {p.team.map((t) => (
                  <div key={t.user_id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-600 truncate">{t.name}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Input
                        type="number" min={0} max={100} defaultValue={t.allocation_pct ?? ""}
                        onBlur={(e) => onSetAllocation(p.id, t.user_id, e.target.value === "" ? null : Number(e.target.value))}
                        className="rounded-sm h-7 w-16 text-xs" placeholder="—"
                        data-testid={`planner-allocation-input-${p.id}-${t.user_id}`}
                      />
                      <span className="text-[11px] text-slate-400">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter><Button size="sm" onClick={onClose} className="rounded-sm">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
